import React from "react";

import { useGetPlanCostsQuery, getDependents } from "EmployeeApplication/api";
import arrayMutators from "final-form-arrays";
import _ from "lodash";
import { Field, Form, FormRenderProps } from "react-final-form";
import { useSelector } from "react-redux";
import formatCurrency from "Utils/formatCurrency";
import { formatNameFL } from "Utils/formatName";
import { EmployeePolicy, LifePlan } from "Utils/types";
import { validateRequired } from "Utils/Validations";

import BodyText from "Cobalt/BodyText";
import Header from "Cobalt/Header";
import LoadingBar from "Cobalt/LoadingBar";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalProps } from "Cobalt/Modal";
import { Radio } from "Cobalt/RadioGroup";
import { FooterTD, FooterTR, HeaderTR, Table, TD, TH, TR } from "Cobalt/TableV2";
import CarrierLogo from "Common/CarrierLogo";
import { SaveButton } from "Common/forms/buttons";
import { Span } from "Common/HTML";

const useCostBreakdown = ({ plan, employeePolicy, employeeCoverageAmount, spouseCoverageAmount }) => {
  const dependents = useSelector(getDependents) || [];
  const dependentIDs = dependents.map((dependent) => dependent.id);

  const { isFetching: isFetchingPlanCosts, data: { planCosts = [] } = {} } = useGetPlanCostsQuery({
    dependentIDs,
    id: employeePolicy.id,
    type: plan.type,
    coverageAmount: employeeCoverageAmount,
    spouseCoverageAmount,
  });

  const planCost = planCosts.find((cost) => cost.planID === plan.id) || {};
  const { pricing = {}, costBreakdowns = {} } = planCost;
  const breakdown = costBreakdowns;
  return {
    breakdown,
    pricing,
    isFetchingPlanCosts,
  };
};

interface ModalContentProps extends FormRenderProps {
  employeePolicy: EmployeePolicy;
  plan: LifePlan;
  title: string;
}

const ModalContent = ({ employeePolicy, title, plan, handleSubmit, values, submitting, touched, errors }: ModalContentProps) => {
  const employeeDependents = useSelector(getDependents) || [];
  const spouse = employeeDependents.find((dependent) => dependent.dependentType === "spouse" || dependent.dependentType === "domestic_partner");
  const children = employeeDependents.filter((dependent) => dependent.dependentType === "child");
  const isEmployeeDeclining = values.employeeEnrolling === "No";
  const isEmployeeEnrolling = values.employeeEnrolling === "Yes";
  const isSpouseEnrolling = values.spouseEnrolling === "Yes";
  const isChildrenEnrolling = values.childrenEnrolling === "Yes";
  const { employeeCoverageAmount, spouseCoverageAmount, childCoverageAmount } = values;
  const { breakdown, pricing, isFetchingPlanCosts } = useCostBreakdown({
    plan,
    employeePolicy,
    employeeCoverageAmount,
    spouseCoverageAmount,
  });

  const totalMonthlyCost = () => {
    if (isEmployeeEnrolling && !isSpouseEnrolling && !isChildrenEnrolling) {
      return pricing.eeOnly;
    }
    if (isEmployeeEnrolling && isSpouseEnrolling && !isChildrenEnrolling) {
      return pricing.eeSpouse;
    }
    if (isEmployeeEnrolling && isChildrenEnrolling && !isSpouseEnrolling) {
      return pricing.eeChildren;
    }
    if (isEmployeeEnrolling && isSpouseEnrolling && isChildrenEnrolling) {
      return pricing.eeFamily;
    }
    return 0;
  };

  return (
    <form onSubmit={handleSubmit}>
      <ModalHeader closeButton>
        <Header level={6} mb={0}>
          {title}
        </Header>
      </ModalHeader>
      <ModalBody>
        <BodyText className="flex items-end" mt={3} mb={6}>
          <CarrierLogo plan={plan} maxSize={3} className="flex-grow" />
          <span className="flex-grow">{plan.display_name}</span>
        </BodyText>
        <Table>
          <thead>
            <HeaderTR>
              <TH>Name</TH>
              <TH>Coverage Amount</TH>
              <TH width="35%">Enroll In Coverage</TH>
              <TH width="60px">Monthly Cost</TH>
            </HeaderTR>
          </thead>
          <tbody>
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>
                {formatNameFL(employeePolicy?.employeeForm)}
                {touched?.employeeEnrolling && errors?.employeeEnrolling && (
                  <Span ml={4} className="text-error-900">
                    {errors.employeeEnrolling}
                  </Span>
                )}
              </TD>
              <TD>
                {employeeCoverageAmount && <BodyText weight="bold">{formatCurrency(employeeCoverageAmount)}</BodyText>}
                {!employeeCoverageAmount && <>No Coverage</>}
              </TD>
              <TD>
                <Field name="employeeEnrolling" type="radio" value="Yes" key="Yes" validate={validateRequired}>
                  {({ input }) => (
                    <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} inline>
                      Yes
                    </Radio>
                  )}
                </Field>
                <Field name="employeeEnrolling" type="radio" value="No" key="No" validate={validateRequired}>
                  {({ input }) => (
                    <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} inline>
                      No
                    </Radio>
                  )}
                </Field>
              </TD>
              <TD>
                {isFetchingPlanCosts ? (
                  <LoadingBar ariaText="Loading Employee Costs" />
                ) : (
                  <span className={isEmployeeDeclining ? "text-gray-500" : ""}>{formatCurrency(breakdown.eeOnly.employeeCost, 2)}</span>
                )}
              </TD>
            </TR>
            {employeeDependents && employeeDependents.length > 0 && (
              <TR highlightFirstCell={false} withBorder={false}>
                <TD colSpan={99} align="left">
                  Family Members:
                  {isEmployeeDeclining && <Span ml={4}>(If you decline coverage, you cannot enroll your family members.)</Span>}
                </TD>
              </TR>
            )}
            {spouse && plan.spouse_coverage && (
              <TR indented highlightFirstCell={false} withBorder={false}>
                <TD>{formatNameFL(spouse)}</TD>
                <TD>
                  {spouseCoverageAmount && <BodyText weight="bold">{formatCurrency(spouseCoverageAmount)}</BodyText>}
                  {!spouseCoverageAmount && <>No Coverage Selected</>}
                </TD>
                <TD>
                  <Field name="spouseEnrolling" type="radio" value="Yes" key="Yes">
                    {({ input }) => (
                      <Radio
                        name={input.name}
                        onChange={input.onChange}
                        value={input.value}
                        checked={input.checked}
                        disabled={isEmployeeDeclining || !spouseCoverageAmount}
                        inline
                      >
                        Yes
                      </Radio>
                    )}
                  </Field>
                  <Field name="spouseEnrolling" type="radio" value="No" key="No">
                    {({ input }) => (
                      <Radio
                        name={input.name}
                        onChange={input.onChange}
                        value={input.value}
                        checked={input.checked}
                        disabled={isEmployeeDeclining || !spouseCoverageAmount}
                        inline
                      >
                        No
                      </Radio>
                    )}
                  </Field>
                </TD>
                <TD>{values.employeeEnrolling === "Yes" && values.spouseEnrolling === "Yes" ? formatCurrency(breakdown?.eeSpouse?.dependentCost, 2) : "--"}</TD>
              </TR>
            )}
            {children && children.length > 0 && plan.child_coverage && childCoverageAmount && (
              <TR indented highlightFirstCell={false} withBorder={false}>
                <TD>Children</TD>
                <TD>
                  <BodyText weight="bold">{formatCurrency(childCoverageAmount)}</BodyText>
                </TD>
                <TD>
                  <Field name="childrenEnrolling" type="radio" value="Yes" key="Yes">
                    {({ input }) => (
                      <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} disabled={isEmployeeDeclining} inline>
                        Yes
                      </Radio>
                    )}
                  </Field>
                  <Field name="childrenEnrolling" type="radio" value="No" key="No">
                    {({ input }) => (
                      <Radio name={input.name} onChange={input.onChange} value={input.value} checked={input.checked} disabled={isEmployeeDeclining} inline>
                        No
                      </Radio>
                    )}
                  </Field>
                </TD>
                <TD>
                  {values.employeeEnrolling === "Yes" && values.childrenEnrolling === "Yes" ? formatCurrency(breakdown?.eeChildren?.dependentCost, 2) : "--"}
                </TD>
              </TR>
            )}
          </tbody>
          <tfoot>
            <FooterTR>
              <FooterTD>Total Monthly Cost</FooterTD>
              <FooterTD>&nbsp;</FooterTD>
              <FooterTD>&nbsp;</FooterTD>
              <FooterTD>
                {isFetchingPlanCosts && <LoadingBar ariaText="Loading Total Costs" />}
                {!isFetchingPlanCosts && formatCurrency(totalMonthlyCost(), 2)}
              </FooterTD>
            </FooterTR>
          </tfoot>
        </Table>
      </ModalBody>
      <ModalFooter>
        <SaveButton submitting={submitting} disabled={!isEmployeeDeclining && !isEmployeeEnrolling} />
      </ModalFooter>
    </form>
  );
};

export interface CoverageEnrollmentFormSubmitValues {
  employeeEnrolling: "Yes" | "No" | null | undefined;
  dependents: Array<{
    dependentID: string;
    enrolling: "Yes" | "No" | null | undefined;
  }>;
  coverageAmount?: number | null;
  spouseCoverageAmount?: number | null;
  childCoverageAmount?: number | null;
}

export interface CoverageEnrollmentFormProps {
  employeeEnrolling: "Yes" | "No" | null | undefined;
  spouseEnrolling?: "Yes" | "No" | null | undefined;
  childrenEnrolling?: "Yes" | "No" | null | undefined;
}

interface CoverageEnrollmentModalProps extends ModalProps {
  employeePolicy: EmployeePolicy;
  plan: LifePlan;
  title: string;
  initialValues: CoverageEnrollmentFormProps;
  onSubmit: (values: CoverageEnrollmentFormSubmitValues) => void;
}

const CoverageEnrollmentModal = ({ employeePolicy, plan, title, initialValues, onSubmit, ...rest }: CoverageEnrollmentModalProps) => {
  return (
    <Modal {...rest}>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        mutators={{
          ...arrayMutators,
        }}
      >
        {(formRenderProps) => <ModalContent employeePolicy={employeePolicy} plan={plan} title={title} {...formRenderProps} />}
      </Form>
    </Modal>
  );
};

export default CoverageEnrollmentModal;
